import React, { useState } from 'react';
import { Button, Paper, Stack, Text } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Account } from '../AccountCard';
import { createToast } from '../Toast';
import { MessageKind } from '../../utils/common';
import { handleApiError } from '../../utils/app';
import { SyncRuleFormValues } from '../sync_rule/interface';
import { getCalendarLabel } from '../sync_rule/calendarOptions';
import { submitSyncRule } from '../sync_rule/submitSyncRule';
import { OnboardingTemplateKey } from '../sync_rule/templates';
import { trackSyncRuleCreated } from '../../utils/analytics';

export const OnboardingReviewCreate: React.FC<{
    form: UseFormReturnType<SyncRuleFormValues>;
    accounts: Account[];
    templateChoice: OnboardingTemplateKey | null;
    onSuccess: () => void;
}> = ({ form, accounts, templateChoice, onSuccess }) => {
    const { t } = useTranslation(['app']);
    const [loading, setLoading] = useState(false);

    const handleCreate = async () => {
        setLoading(true);
        try {
            const response = await submitSyncRule(form.values, null);
            if (!response.ok) {
                handleApiError(response, t, true);
                return;
            }
            trackSyncRuleCreated('onboarding', true);
            createToast(t('dashboard.onboarding.success.toast'), MessageKind.Info);
            onSuccess();
        } catch (error) {
            console.error('Error creating sync rule:', error);
            createToast(t('dashboard.sync.error.unexpected'), MessageKind.Error);
        } finally {
            setLoading(false);
        }
    };

    const templateLabel = templateChoice === 'busy'
        ? t('dashboard.onboarding.step4.busy-title')
        : t('dashboard.onboarding.step4.copy-title');

    return (
        <Stack gap="lg">
            <Stack gap="xs">
                <Text size="xl" fw={600}>{t('dashboard.onboarding.step7.title')}</Text>
                <Text c="dimmed">{t('dashboard.onboarding.step7.description')}</Text>
            </Stack>
            <Paper p="md" withBorder radius="md">
                <Stack gap="sm">
                    <Text>
                        <Text span fw={600}>{t('dashboard.sync.draft.source')}: </Text>
                        {getCalendarLabel(accounts, form.values.source)}
                    </Text>
                    <Text>
                        <Text span fw={600}>{t('dashboard.sync.draft.destination')}: </Text>
                        {getCalendarLabel(accounts, form.values.destination)}
                    </Text>
                    <Text>
                        <Text span fw={600}>{t('dashboard.onboarding.step7.template')}: </Text>
                        {templateLabel}
                    </Text>
                    <Text>
                        <Text span fw={600}>{t('dashboard.sync.label-custom-title')}: </Text>
                        {form.values.summary}
                    </Text>
                </Stack>
            </Paper>
            <Button
                size="md"
                leftSection={<IconCheck size={18} />}
                loading={loading}
                onClick={handleCreate}
            >
                {t('dashboard.onboarding.step7.create')}
            </Button>
        </Stack>
    );
};
